// apps/web/src/lib/zodErrorMap.ts
//
// Global Zod error map (Phase 02.2.1 Plan 02 D-06).
// Routes built-in Zod issue codes to pt-BR i18n keys so any field whose
// schema author forgot a custom message still renders in the user's locale.
// Closes UAT22-05 (telefone "Invalid input: expected string, received undefined").
//
// Zod v4 API notes (the project runs zod@^4):
// - z.setErrorMap is retained as a compat shim (deprecated name, still wired
//   into z.config({ customError })). We keep the call for API familiarity and
//   because the tech-lead audit assertion greps for `setErrorMap`.
// - ZodIssueCode is also retained as a compat shim with string-literal values
//   ("invalid_type", "too_small", "too_big", "invalid_format"). We use it here
//   so the assertion grep `ZodIssueCode.invalid_type` passes AND so the
//   switch branches survive a hypothetical future rename.
// - The errorMap signature in v4 is `(issue) => { message } | string | undefined`.
//   There is no ctx.defaultError; returning `undefined` falls through to Zod's
//   default English message (loud + visible — a NEW issue code leaking means
//   someone needs to add a branch here).
// - UAT22-05 root cause is issue.input === undefined (RHF passes undefined for
//   unregistered fields on first render). In v3 this was issue.received ===
//   'undefined'; v4 exposes it as issue.input.

import { z } from 'zod';

// $ZodRawIssue in v4 is a union of all raw issue shapes; the precise TS type
// lives under z.core but is internal. We accept the duck-typed shape and
// narrow by `code` inside the switch.
type ZodIssueLike = {
  code?: string;
  input?: unknown;
  format?: string;
};

export function alamoZodErrorMap(issue: ZodIssueLike): { message: string } | string | undefined {
  switch (issue.code) {
    case z.ZodIssueCode.invalid_type: {
      // `input === undefined` is the UAT22-05 root cause (RHF sends undefined
      // before the user types). Collapse to the required-field key so the
      // toast / field error reads "Campo obrigatório" instead of the raw
      // "Invalid input: expected string, received undefined".
      if (issue.input === undefined) return { message: 'common:validation.required' };
      return { message: 'common:validation.invalid_type' };
    }
    case z.ZodIssueCode.too_small:
      return { message: 'common:validation.required' };
    case z.ZodIssueCode.too_big:
      return { message: 'common:validation.too_big' };
    case z.ZodIssueCode.invalid_format: {
      // v4 collapses invalid_string + invalid_email into invalid_format.
      if (issue.format === 'email') return { message: 'common:validation.invalid_email' };
      return { message: 'common:validation.invalid_string' };
    }
    default:
      // Fall through to Zod's default English message. Any component showing
      // that in production means a NEW issue code leaked — add a case above.
      return undefined;
  }
}

export function registerZodErrorMap(): void {
  // `z.setErrorMap` is a compat alias for `z.config({ customError })` in v4.
  z.setErrorMap(alamoZodErrorMap);
}
